import React, { useEffect, useState } from 'react';
import { Button, Card } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/pt-br';
import { extractTransectDataFromMap } from '../../../common/map';
import SimpleInfoDialog from '../dialog/SimpleInfoDialog';
import { IconButton } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import TimelineOutlinedIcon from '@material-ui/icons/TimelineOutlined';

/**
 * @function MeanTransectButton
 * @description React component that computes and displays a chart of the average shoreline
 * transect position over time, aggregated across all transects for each date.
 * @see {@link handleMeanTransect} - builds the chart data from the transect matrix.
 * @see {@link chartOptions} - builds the chart.js options.
 * **Called by:
 * @see {ProcessingPage} - Shoreline module's processing page.
 * @see {ProcessingShoreRPage} - ShoreR module's processing page.
 * @memberof module:SHARED
 * @returns {JSX.Element} The mean transect button and its chart popup.
 */
const MeanTransectButton = () => {
  const [t] = useTranslation();
  const theme = useTheme();
  const chartLang = t('forms.transectEvolution.chartLanguage');
  const charLanguage = chartLang.toLowerCase().includes('portugu');
  moment.locale(charLanguage ? 'pt-BR' : 'en');

  const [showHelpDialog, setShowHelpDialog] = useState(false);

  const [showPopup, setShowPopup] = useState(false);
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (!showPopup) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setShowPopup(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showPopup]);

  /**
   * @function handleMeanTransect
   * @description Extracts the transect matrix, then for each unique date across all transects
   * averages the distance readings from every transect that has a value for that date, building
   * a chart.js dataset of the average position over time.
   * @see {@link extractTransectDataFromMap}
   * **Called by:
   * @see {MeanTransectButton} - via the "Mean transect" button's onClick.
   * @memberof module:SHARED
   * @returns {void}
   */
  const handleMeanTransect = () => {
    const { transectMatrix } = extractTransectDataFromMap();
    if (!transectMatrix || transectMatrix.length === 0) return;

    const allDatesSet = new Set();
    transectMatrix.forEach((t) => t.distances.forEach((d) => d.date && allDatesSet.add(d.date)));

    const allDates = Array.from(allDatesSet).sort();
    const averageByDate = allDates.map((date) => {
      const values = transectMatrix
        .map((t) => {
          const match = t.distances.find((d) => d.date === date);
          return match ? match.distance : null;
        })
        .filter((v) => v !== null);
      const sum = values.reduce((a, b) => a + b, 0);
      return values.length > 0 ? sum / values.length : null;
    });

    const dataset = {
      label: t('forms.transectEvolution.averagePosition'),
      data: averageByDate,
      showLine: true,
      fill: false,
      borderColor: '#1976d2',
      pointBackgroundColor: '#1976d2',
    };

    setChartData({
      labels: allDates,
      datasets: [dataset],
    });

    setShowPopup(true);
  };

  /**
   * @function chartOptions
   * @description Builds the chart.js options for the mean transect chart. The X-axis time unit
   * scales with the number of dates (day, month, or year), and both the Y-axis ticks and tooltip
   * values use a comma as the decimal separator for pt-BR, or a dot for en.
   * @param {boolean} [charLanguage=false] - Whether to format numbers/dates using Portuguese conventions.
   * @param {number} [dateCount=0] - Number of distinct dates in the dataset, used to pick the time unit.
   * **Called by:
   * @see {MeanTransectButton} - via the Line component's options prop.
   * @memberof module:SHARED
   * @returns {Object} Chart.js options configuration.
   */
  const chartOptions = (charLanguage = false, dateCount = 0) => {
    let timeUnit = 'day';
    if (dateCount > 60) timeUnit = 'month';
    if (dateCount > 300) timeUnit = 'year';

    return {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: false,
              callback: function (value) {
                return charLanguage ? value.toFixed(2).replace('.', ',') : value.toFixed(2);
              },
            },
          },
        ],
        xAxes: [
          {
            type: 'time',
            time: {
              parser: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
              tooltipFormat: charLanguage ? 'DD/MM/YYYY' : 'MM/DD/YYYY',
              unit: timeUnit,
              displayFormats: {
                day: charLanguage ? 'DD/MM/YYYY' : 'MM/DD/YYYY',
                month: charLanguage ? 'MM/YYYY' : 'MMM YYYY',
                year: 'YYYY',
              },
            },
            ticks: {
              source: 'data',
              autoSkip: true,
              maxTicksLimit: 12,
              maxRotation: 45,
              minRotation: 15,
            },
          },
        ],
      },
      legend: {
        position: 'top',
      },
      title: {
        display: false,
        text: t('forms.transectEvolution.averagePosition'),
      },
      tooltips: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.value;
            return charLanguage
              ? `Valor: ${parseFloat(value).toFixed(2).replace('.', ',')}`
              : `Value: ${parseFloat(value).toFixed(2)}`;
          },
        },
      },
      elements: {
        line: {
          tension: 0,
        },
      },
    };
  };

  return (
    <>
      <Button
        fullWidth
        color="primary"
        variant="contained"
        startIcon={<TimelineOutlinedIcon />}
        onClick={handleMeanTransect}>
        {t('forms.transectEvolution.meanTransect')}
      </Button>

      <SimpleInfoDialog
        open={showHelpDialog}
        close={() => setShowHelpDialog(false)}
        title={t('forms.transectEvolution.meanTransect')}
        text={t('forms.transectEvolution.MeanTransectDialog')}
      />

      {showPopup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            padding: '24px',
          }}>
          <div
            style={{
              background: theme.palette.background.paper,
              color: theme.palette.text.primary,
              padding: '24px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '700px',
              boxShadow: '0 0 20px rgba(0,0,0,0.3)',
              textAlign: 'center',
              position: 'relative',
            }}>
            <div style={{ position: 'absolute', top: 12, right: 12 }}>
              <IconButton color="primary" onClick={() => setShowHelpDialog(true)} style={{ padding: 6 }}>
                <HelpOutlineIcon />
              </IconButton>
            </div>

            <h3>{t('forms.transectEvolution.chartTitle')}</h3>

            <div style={{ height: '300px', marginBottom: '24px' }}>
              {chartData ? (
                <Line data={chartData} options={chartOptions(charLanguage, chartData.labels.length)} />
              ) : (
                <p>{t('forms.transectEvolution.noData')}</p>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <Button variant="outlined" color="secondary" onClick={() => setShowPopup(false)}>
                {t('forms.shorelineAnalysis.close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MeanTransectButton;
